import { rem } from "polished";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ThemeKind } from "src/types";
import styled from "styled-components";

import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import useTheme from "@/components/editor/themes/use-theme";
import { getInvertedColor } from "@/components/editor/utils/colors";
import { ColorLabels } from "@/components/editor/utils/use-color";
import { Close } from "@/components/icons/close";
import useViewportContext from "@/hooks/use-viewport-context";

type Props = {
  onClear?: () => void;
  onClickPart: (part: string) => void;
  onClickTag: (tag: string) => void;
  parts: string[];
  selectedParts: string[];
  selectedTags: string[];
  tags: string[];
  onClose: () => void;
  rounded?: boolean;
};

export default function PartsTagsDisplay(props: Props) {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const { viewport } = useViewportContext();
  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);

  const isClearAllShown = React.useMemo(
    () => [...props.selectedParts, ...props.selectedTags].length > 0,
    [props.selectedParts, props.selectedTags]
  );

  return (
    <>
      {isMobile && (
        <div className="flex justify-end">
          <CloseButton
            aria-label={t("schedule.close-menu")}
            onClick={props.onClose}
            className={currentTheme.kind}
          >
            <Close className="size-6" />
          </CloseButton>
        </div>
      )}
      <div className="flex justify-between" data-testid="parts-tags-display">
        <MenuSection className={currentTheme.kind}>
          {isMobile ? t("schedule.segments") : t("schedule.segments-caps")}
        </MenuSection>
        {isClearAllShown && (
          <ClearAllButton onClick={props.onClear} className={currentTheme.kind}>
            {isMobile ? t("schedule.clear-all") : t("common.clear-all")}
          </ClearAllButton>
        )}
      </div>
      {props.parts?.length > 0 && (
        <FilterTagsContainer className={currentTheme.kind}>
          {props.parts.map((part) => (
            <Tag
              key={part}
              label={part}
              isPressed={props.selectedParts.includes(part)}
              onClick={() => props.onClickPart(part)}
              data-testid="event-part"
            />
          ))}
        </FilterTagsContainer>
      )}
      {props.tags?.length > 0 && (
        <>
          <MenuSection className={currentTheme.kind}>
            {isMobile ? t("schedule.tags") : t("schedule.tags-caps")}
          </MenuSection>
          <FilterTagsContainer className={currentTheme.kind}>
            {props.tags.map((tag) => (
              <Tag
                key={tag}
                label={tag}
                isPressed={props.selectedTags.includes(tag)}
                onClick={() => props.onClickTag(tag)}
                data-testid="event-tag"
              />
            ))}
          </FilterTagsContainer>
        </>
      )}
      {isMobile && (
        <div className="flex flex-col justify-center">
          <ShowResultsButton
            onClick={props.onClose}
            rounded={props.rounded}
            className={currentTheme.kind}
          >
            {t("schedule.show-results")}
          </ShowResultsButton>
        </div>
      )}
    </>
  );
}

const MenuSection = withViewportStyle("div")`
  display: flex;
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  font-size: ${rem(14)};
  line-height: ${rem(32)};
  font-weight: 600;
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};

  ${(props) => props.$viewport.mobile`
    font-size: ${rem(24)};
    font-weight: 500;
  `};

  &.${ThemeKind.NOLAN} {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }
`;

const FilterTagsContainer = withViewportStyle("div")`
  display: flex;
  flex-wrap: wrap;

   ${(props) => props.$viewport.mobile`
    margin-top: ${rem(24)};
  `};

  &.${ThemeKind.NOLAN} {
    background: ${({ theme }) => theme.colors[ColorLabels.VISUALS_2]};
  }

  &.${ThemeKind.DEFAULT} {
    --backgroundColor: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
    --invertedBackgroundColor: ${({ theme }) =>
      getInvertedColor(theme.colors[ColorLabels.BACKGROUND])};

    button {
      &[aria-pressed="false"] {
        background: var(--backgroundColor) !important;
        color: var(--invertedBackgroundColor) !important;
        border: ${rem(1)} solid var(--invertedBackgroundColor) !important;
      }

      &[aria-pressed="true"] {
        color: var(--backgroundColor) !important;
        background: var(--invertedBackgroundColor) !important;
      }
    }
  }
`;

const CloseButton = styled.button`
  padding: ${rem(8)};
  background: none;
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};

  &.${ThemeKind.NOLAN} {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }
`;

const ClearAllButton = withViewportStyle("button")`
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  background: none;
  font-size: ${rem(14)};
  line-height: ${rem(24)};
  z-index: var(--z-index-base);
  font-weight: 500;
  padding: 0;
 

  &:hover {
    background: none;
  }

  ${(props) => props.$viewport.mobile`
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    font-size: ${rem(14)};
    line-height: ${rem(24)};
    font-weight: 500;
    text-transform: uppercase;

    &.${ThemeKind.CROSBY} {
      color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
    }
  `};

  &.${ThemeKind.NOLAN} {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }
`;

const ShowResultsButton = styled.button<{ rounded?: boolean }>`
  color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  font-size: ${rem(15)};
  line-height: ${rem(24)};
  height: 2rem;
  font-weight: 500;
  border-radius: ${({ rounded }) => (rounded ? rem(200) : 0)};
  border: none;

  &:hover {
    color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
    background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    border: none;
  }

  &.${ThemeKind.CROSBY} {
    color: ${({ theme }) =>
      getInvertedColor(theme.colors[ColorLabels.PRIMARY])};
  }

  &.${ThemeKind.NOLAN} {
    color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
    background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    border: none;

    :hover {
      color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
      background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    }
  }
`;

interface TagProps {
  label: string;
  isPressed: boolean;
  onClick: () => void;
  "data-testid"?: string;
}

function Tag({ label, isPressed, onClick, "data-testid": testId }: TagProps) {
  return (
    <StyledTag aria-pressed={isPressed} onClick={onClick} data-testid={testId}>
      {label}
    </StyledTag>
  );
}

const StyledTag = styled.button`
  margin-right: ${rem(8)};
  margin-bottom: ${rem(16)};
  padding: ${rem(4)} ${rem(12)};
  font-size: ${rem(15)};
  line-height: ${rem(24)};
  font-weight: 500;
  cursor: pointer;

  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};

  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  background: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
  border: ${rem(1)} solid ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};

  &[aria-pressed="true"] {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
    background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    border-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  }

  .${ThemeKind.CROSBY} & {
    border-radius: ${rem(200)};

    &[aria-pressed="true"] {
      color: ${({ theme }) => theme.colors[ColorLabels.VISUALS_2] || "#fff"};
    }
  }

  .${ThemeKind.NOLAN} & {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    background: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
    border-color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};

    &[aria-pressed="true"] {
      color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
      background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
      border-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    }
  }
 }
`;
